import { Fragment } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import LineThumbsUpIcon from 'LuxKit/Icons/line/LineThumbsUpIcon'
import { excludeNullOrUndefined } from 'checkout/utils'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'

const Container = styled.div`
  padding: ${rem(12)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const RecommendationWrapper = styled(Group)`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
`

const Separator = styled.span`
  margin: 0 ${rem(4)};
`

interface Props {
  recommendedForText: string
  showRecommendation?: boolean
  rooms: Array<{
    name: string
    bedConfiguration: string | undefined
    size?: string
  }>
  entireHome?: {
    numberOfGuests: number,
    numberOfBedrooms: number,
    numberOfBathrooms: number
  }
}

function RoomBreakdownDetails({
  recommendedForText,
  showRecommendation = true,
  rooms,
  entireHome,
}: Props) {
  const intl = useIntl()
  return (
    <Container>
      <Group direction="vertical" gap={8}>
        {showRecommendation &&
          <RecommendationWrapper direction="horizontal" gap={4}>
            <Group horizontalAlign="center" verticalAlign="center" direction="horizontal">
              <LineThumbsUpIcon size={16} colour="highlight-primary-dark" />
            </Group>

            <BodyText variant="small" weight="bold" colour="highlight-primary-dark">
              {recommendedForText}
            </BodyText>
          </RecommendationWrapper>}
        {!entireHome && rooms.map((room, roomIndex) => {
          const configItems = [room.bedConfiguration, room.size].filter(excludeNullOrUndefined)

          return (
            <Group key={roomIndex} direction="vertical" gap={2}>
              <BodyText variant="small" weight="bold" colour="normal-contrast">
                {room.name}
              </BodyText>
              <BodyText variant="small" colour="light-foreground-alt">
                {configItems.map((item, index) => (
                  <Fragment key={index}>
                    {index > 0 && <Separator>{intl.formatMessage(middleDotSeparatorMessage)}</Separator>}
                    {item}
                  </Fragment>
                ))}
              </BodyText>
            </Group>
          )
        })}
        {!!entireHome &&
          <Group direction="vertical" gap={2}>
            <BodyText variant="small" weight="bold" colour="neutral-one">
              {intl.formatMessage({
                id: 'LJptO8EKd1X1t5-8bPAq',
                defaultMessage: 'Entire Home',
              })}
            </BodyText>
            <BodyText variant="small" colour="neutral-two">
              {intl.formatMessage({
                id: '0Z3jHIzfiAbwyB5XT3FB',
                defaultMessage: 'Up to {guestCountLabel} · {bedroomCountLabel} · {bathroomCountLabel}',
              }, {
                guestCountLabel: intl.formatMessage({
                  id: 'R3019BzeZYCjSRIYIlGe',
                  defaultMessage: '{guestCount, plural, one {# guest} other {# guests}}',
                }, { guestCount: entireHome.numberOfGuests }),
                bedroomCountLabel: intl.formatMessage({
                  id: 'Dm_CF9Sswqlzy_KHQiT-',
                  defaultMessage: '{bedroomCount, plural, one {# bedroom} other {# bedrooms}}',
                }, { bedroomCount: entireHome.numberOfBedrooms }),
                bathroomCountLabel: intl.formatMessage({
                  id: 'nlGNE-akGqF7kc3Iaqf7',
                  defaultMessage: '{bathroomCount, plural, one {# bathroom} other {# bathrooms}}',
                }, { bathroomCount: entireHome.numberOfBathrooms }),
              })}
            </BodyText>
          </Group>}
      </Group>
    </Container>
  )
}

export default RoomBreakdownDetails
